import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../app/store";
import moment from "moment";

/* ================== TYPES ================== */

export interface AuthUserType {
  id: string;
  name: string;
  email: string;
  role: string;
  outstandingBalance: number;
}

export interface StateAuthType {
  user: AuthUserType | null;
  token: string;
  msg: string;
  isLogIn: boolean;
}

export interface AuthType {
  email: string;
  password: string;
}

interface AuthResponse {
  user: AuthUserType;
  token: string;
  msg: string;
}

/* ================== INITIAL STATE ================== */

const storedUser = localStorage.getItem("user");
const storedToken = localStorage.getItem("token");

const initialState: StateAuthType = {
  user: storedUser ? JSON.parse(storedUser) : null,
  token: storedToken || "",
  msg: "",
  isLogIn: !!storedUser,
};

/* ================== THUNK ================== */

export const authUser = createAsyncThunk<
  AuthResponse,
  AuthType,
  { rejectValue: { msg: string } }
>("auth/logIn", async (userObject, { rejectWithValue }) => {
  try {
    const response = await axios.post(
      "/api/auth/",
      userObject,
      { headers: { "Content-Type": "application/json" } }
    );

    const user: AuthUserType = {
      id: response.data.user.id,
      name: response.data.user.name,
      email: response.data.user.email,
      role: response.data.user.role,
      outstandingBalance: response.data.user.outstandingBalance,
    };

    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("token", response.data.token);
    localStorage.setItem("signin-date", moment().format());

    return {
      user,
      token: response.data.token,
      msg: response.data.msg,
    };
  } catch (err) {
    const error = err as any;
    return rejectWithValue({
      msg: error.response?.data?.msg || "Błąd logowania",
    });
  }
});

/* ================== SLICE ================== */

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = "";
      state.msg = "";
      state.isLogIn = false;

      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },

    clearMsg: (state) => {
      state.msg = "";
    },

  },

  extraReducers: (builder) => {
    builder
      .addCase(authUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.msg = action.payload.msg;
        state.isLogIn = true;
      })
      .addCase(authUser.rejected, (state, action) => {
        if (action.payload) {
          state.msg = action.payload.msg;
        }
      });
  },
});

/* ================== EXPORTS ================== */

export const { logout, clearMsg } = authSlice.actions;

export const selectUser = (state: RootState) => state.auth.user;
export const selectIsLogIn = (state: RootState) => state.auth.isLogIn;
export const selectAuthMsg = (state: RootState) => state.auth.msg;

export default authSlice.reducer;